import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

const SCHOOL_NAME = "The Rising School";
const CUTOFF = "10:00 AM";
const GREEN = [209, 250, 229], YELLOW = [254, 243, 199], RED = [254, 226, 226];

const fmtTime = (t) => (t ? new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "-");
const dayLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short" });
const byClass = (rows) => [...(rows || [])].sort((a, b) => String(a.classId).localeCompare(String(b.classId), undefined, { numeric: true }));

function newDoc(title, subtitle, landscape = false) {
  const pdf = new jsPDF({ orientation: landscape ? "landscape" : "portrait", unit: "pt", format: "a4" });
  pdf.setFontSize(16); pdf.setTextColor(15, 23, 42); pdf.text(`${SCHOOL_NAME} - ${title}`, 28, 36);
  pdf.setFontSize(10); pdf.setTextColor(100, 116, 139); pdf.text(subtitle, 28, 52);
  return pdf;
}
const table = (pdf, head, body, opts = {}) =>
  autoTable(pdf, { startY: 66, head: [head], body, styles: { fontSize: 9, cellPadding: 4 }, headStyles: { fillColor: [15, 23, 42] }, ...opts });

/* ---------------- Attendance ---------------- */
export function attendanceStats(comp, daily) {
  const rows = byClass(comp?.rows);
  const submitted = rows.filter((r) => r.attendanceDone);
  const pending = rows.filter((r) => !r.attendanceDone);
  const present = daily ? daily.present : submitted.reduce((s, r) => s + r.presentCount, 0);
  const total = daily ? daily.total : submitted.reduce((s, r) => s + r.totalStudents, 0);
  return { rows, submitted, pending, present, total, pct: total ? Math.round((present / total) * 1000) / 10 : 0 };
}

export function exportAttendancePdf({ date, comp, daily, audit, view = "daily" }) {
  const s = attendanceStats(comp, daily);
  if (view === "7day" && audit?.matrix?.length) {
    const days = audit.days;
    const pdf = newDoc("Attendance Audit (7 Days)", `Last 7 working days up to ${date}`, true);
    table(pdf, ["Class", "Teacher", ...days.map(dayLabel), "Attendance %"],
      byClass(audit.matrix).map((m) => [m.classId, m.teacherName,
        ...m.stats.map((x) => (x.holiday ? "H" : ["On-Time", "Late"].includes(x.status) ? "OK" : x.status === "Pending" ? "-" : "X")),
        `${m.attendanceRate}%`]),
      { didParseCell: (d) => {
        if (d.section !== "body" || d.column.index < 2 || d.column.index >= 2 + days.length) return;
        d.cell.styles.fillColor = d.cell.raw === "OK" ? GREEN : d.cell.raw === "X" ? RED : d.cell.raw === "-" ? YELLOW : [238, 242, 255];
        d.cell.styles.halign = "center";
      } });
    return pdf.save(`attendance-audit-7day-${date}.pdf`);
  }
  const pdf = newDoc("Attendance Audit", `Date: ${date} | Overall: ${s.present}/${s.total} (${s.pct}%)`);
  table(pdf, ["Class", "Teacher", "Time", "Present", "Absent", "Leave", "Status"],
    s.rows.map((r) => [r.classId, r.teacherName || "Unassigned", r.attendanceDone ? fmtTime(r.attendanceSubmittedAt || r.submittedAt) : "-",
      r.attendanceDone ? r.presentCount : "-", r.attendanceDone ? r.absentCount : "-", r.attendanceDone ? r.leaveCount : "-",
      r.attendanceDone ? "Submitted" : "Pending"]),
    { didParseCell: (d) => { if (d.section === "body" && d.column.index === 6) d.cell.styles.fillColor = d.cell.raw === "Submitted" ? GREEN : RED; } });
  pdf.save(`attendance-audit-${date}.pdf`);
}

export function attendanceWhatsappText({ date, comp, daily }) {
  const s = attendanceStats(comp, daily);
  const sub = s.submitted.map((r) => `• ${r.classId} (${r.teacherName || "Unassigned"}): ${r.presentCount}/${r.totalStudents} (${fmtTime(r.attendanceSubmittedAt || r.submittedAt)})`);
  const pen = s.pending.map((r) => `• ${r.classId} (${r.teacherName || "Unassigned"}): Attendance Not Marked`);
  return [
    "📋 *DAILY ATTENDANCE COMPLIANCE REPORT*", `🏫 *${SCHOOL_NAME}*`, `📅 Date: ${date}`,
    `📊 Overall Attendance: ${s.present}/${s.total} (${s.pct}%)`,
    `✅ Submitted Classes (${s.submitted.length}):`, ...sub,
    `🔴 Pending Classes (${s.pending.length}):`, ...pen
  ].join("\n");
}

/* ---------------- Attendance-only (PDF + WhatsApp) ---------------- */
const IST = "Asia/Kolkata";
const CUTOFF_MINUTES = 10 * 60; // 10:00 AM IST
const fmtIst = (t) => new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: IST }).toUpperCase();
const istMinutes = (t) => {
  const p = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: IST }).formatToParts(new Date(t));
  return Number(p.find((x) => x.type === "hour").value) * 60 + Number(p.find((x) => x.type === "minute").value);
};

/** Real status from the stored submission timestamp: { key, time, label, icon }. Never hardcoded. */
export function attendanceStatusOf(r) {
  const at = r.attendanceSubmittedAt || r.submittedAt;
  if (!r.attendanceDone || !at) return { key: "Pending", time: "", label: "Pending", icon: "\u{1F534}" };
  const time = fmtIst(at);
  return istMinutes(at) <= CUTOFF_MINUTES
    ? { key: "On-Time", time, label: `On-Time (${time})`, icon: "\u{1F7E2}" }
    : { key: "Late", time, label: `Late (${time})`, icon: "\u{1F7E1}" };
}

const attPct = (r) => (r.attendanceDone && r.totalStudents ? `${Math.round((r.presentCount / r.totalStudents) * 1000) / 10}%` : "-");

export function exportAttendanceOnlyPdf({ date, comp, daily }) {
  const s = attendanceStats(comp, daily);
  const pdf = newDoc("Attendance Report", `Date: ${date} | Cutoff: ${CUTOFF} IST | Overall: ${s.present}/${s.total} (${s.pct}%)`);
  table(pdf, ["Class", "Teacher", "Submission Time & Status", "Present", "Absent", "Leave", "Attendance %"],
    s.rows.map((r) => [r.classId, r.teacherName || "Unassigned", attendanceStatusOf(r).label.replace(/^Pending$/, "Pending"),
      r.attendanceDone ? r.presentCount : "-", r.attendanceDone ? r.absentCount : "-", r.attendanceDone ? r.leaveCount : "-", attPct(r)]),
    { didParseCell: (d) => {
      if (d.section !== "body" || d.column.index !== 2) return;
      const v = String(d.cell.raw);
      d.cell.styles.fillColor = v.startsWith("On-Time") ? GREEN : v.startsWith("Late") ? YELLOW : RED;
    } });
  pdf.save(`attendance-report-${date}.pdf`);
}

export function attendanceOnlyWhatsappText({ date, comp, daily }) {
  const s = attendanceStats(comp, daily);
  const sum = (k) => s.submitted.reduce((a, r) => a + (r[k] || 0), 0);
  const late = s.submitted.filter((r) => attendanceStatusOf(r).key === "Late");
  return [
    "\u{1F4CB} *DAILY ATTENDANCE REPORT*", `\u{1F3EB} *${SCHOOL_NAME}*`, `\u{1F4C5} Date: ${date}`,
    `\u{1F4CA} Overall: ${s.present}/${s.total} (${s.pct}%)`,
    `\u2705 Present: ${sum("presentCount")}`, `\u274C Absent: ${sum("absentCount")}`, `\u{1F7E1} Approved Leave: ${sum("leaveCount")}`,
    `\u{1F7E1} Late Submissions (${late.length}):`, ...late.map((r) => `\u2022 ${r.classId} (${r.teacherName || "Unassigned"}): ${attendanceStatusOf(r).time}`),
    `\u{1F534} Pending / Delinquent Classes (${s.pending.length}):`, ...s.pending.map((r) => `\u2022 ${r.classId} (${r.teacherName || "Unassigned"})`)
  ].join("\n");
}

/* ---------------- Diary ---------------- */
export function exportDiaryPdf({ date, comp }) {
  const rows = byClass(comp?.rows);
  const done = rows.filter((r) => r.diaryDone).length;
  const pdf = newDoc("Diary Audit", `Date: ${date} | Posted: ${done}/${rows.length} | Missing: ${rows.length - done}`);
  table(pdf, ["Class", "Teacher", "Diary / Homework", "Time"],
    rows.map((r) => [r.classId, r.teacherName || "Unassigned", r.diaryDone ? "Posted" : "NOT POSTED", r.diaryDone ? fmtTime(r.diarySubmittedAt || r.submittedAt) : "-"]),
    { didParseCell: (d) => { if (d.section === "body" && d.column.index === 2) d.cell.styles.fillColor = d.cell.raw === "Posted" ? GREEN : RED; } });
  pdf.save(`diary-audit-${date}.pdf`);
}

export function diaryWhatsappText({ date, comp }) {
  const rows = byClass(comp?.rows);
  const ok = rows.filter((r) => r.diaryDone), no = rows.filter((r) => !r.diaryDone);
  return [
    "📓 *DAILY DIARY COMPLIANCE REPORT*", `🏫 *${SCHOOL_NAME}*`, `📅 Date: ${date}`,
    `✅ Homework Posted (${ok.length}):`, ...ok.map((r) => `• ${r.classId} (${r.teacherName || "Unassigned"}): ${fmtTime(r.diarySubmittedAt || r.submittedAt)}`),
    `🔴 Not Posted (${no.length}):`, ...no.map((r) => `• ${r.classId} (${r.teacherName || "Unassigned"}): Homework Not Posted`)
  ].join("\n");
}

export const shareOnWhatsapp = (text) => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");

/* ---------------- Timeliness ---------------- */
export function exportTimelinessPdf({ date, comp, audit }) {
  const punctuality = new Map((audit?.matrix || []).map((m) => [m.classId, m.punctualityRate]));
  const pdf = newDoc("Timeliness Log", `Date: ${date} | Cutoff: ${CUTOFF} | Green = on time, Yellow = pending, Red = late`);
  table(pdf, ["Teacher", "Class", "Submitted", "Status", "7-Day On-Time"],
    byClass(comp?.rows).map((r) => [r.teacherName || "Unassigned", r.classId,
      r.attendanceDone ? fmtTime(r.attendanceSubmittedAt || r.submittedAt) : "-",
      !r.attendanceDone ? "Pending" : r.isLate ? "Late" : "On-Time",
      punctuality.has(r.classId) ? `${punctuality.get(r.classId)}% On-Time` : "-"]),
    { didParseCell: (d) => {
      if (d.section !== "body") return;
      const st = d.row.raw[3];
      if (d.column.index === 3) d.cell.styles.fillColor = st === "On-Time" ? GREEN : st === "Late" ? RED : YELLOW;
      if (d.column.index === 4) { const v = parseFloat(d.cell.raw); if (!isNaN(v)) d.cell.styles.fillColor = v >= 85 ? GREEN : v >= 60 ? YELLOW : RED; }
    } });
  pdf.save(`timeliness-log-${date}.pdf`);
}

/* ---------------- Analytics snapshot ---------------- */
export async function saveAnalyticsSnapshot(node, { date, format = "png" }) {
  const canvas = await html2canvas(node, { backgroundColor: "#ffffff", scale: 2, useCORS: true });
  if (format === "png") {
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png"); a.download = `analytics-snapshot-${date}.png`; a.click();
    return;
  }
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const W = pdf.internal.pageSize.getWidth() - 40, H = pdf.internal.pageSize.getHeight() - 40;
  const ratio = Math.min(W / canvas.width, H / canvas.height);
  pdf.addImage(canvas.toDataURL("image/png"), "PNG", 20, 20, canvas.width * ratio, canvas.height * ratio);
  pdf.save(`analytics-snapshot-${date}.pdf`);
}